/**
 * WERSJA „OBECNA” (produkcja 0.31.3) — karta pojazdu, kopia z `main`
 * (`git show origin/main:apps/app/src/admin/fleet/CarShow.tsx`), 12.10.2026.
 *
 * Decyzja użytkownika 12.10.2026: „obecna” wersja aplikacji ma zachowywać
 * się dokładnie jak produkcja, a zmiany z develop są za przełącznikami.
 * Z tego pliku korzysta karta pojazdu przy wyłączonym przełączniku
 * `vehicleCardPrototype` (`fleet/CarShow.tsx`). Zmieniamy go tylko po to,
 * żeby wpiąć inne przełączniki (sekcja domyślnie niewidoczna) — przy
 * wszystkich wyłączonych ekran ma być taki jak na produkcji. Po decyzji
 * „wchodzi” albo „odrzucone” katalog `legacy/` znika razem z przełącznikiem.
 */
import { Show, Title, useRecordContext, RaRecord } from 'react-admin';
import { Alert, Card, CardContent, Stack, Table, TableBody, Box, Typography } from '@mui/material';
import type { CardProps } from '@mui/material';
import { CarEligibilities } from './CarEligibilities';
import { CarOrderSuggestions } from './CarOrderSuggestions';
import { VehicleSchedule } from './VehicleSchedule';
import { SectionCard, Row, fmtCalendarDate } from '../../../shared/Panel';
import {
  AvailabilityCell,
  CompanyCell,
  PayloadCell,
  DriverCell,
  LinkedVehicleCell,
  ForkliftCell,
  formatDimensions,
} from '../cells';
import { vehicleTypeLabel } from '../vehicleTypes';
import { ModuleGate, companyIdOf } from '../../ModuleGate';
import { PermissionGate } from '../../PermissionGate';
import { ELEMENT } from '../../roleCenter/elementKeys';
import { FeatureGate } from '../../featureFlags/FeatureGate';
import { CAR_CHANGE_HISTORY, MANUAL_ADR } from '../fleetFeatures';
import { CarChangeHistory } from '../CarChangeHistory';
import { ManualAdr } from '../ManualAdr';

type CarRecord = RaRecord & Record<string, unknown>;

/**
 * Zlecenia dla tego pojazdu — moduł rekomendacji spółki POJAZDU (T.01,
 * 06.10.2026); wyłączony — sekcji nie ma, API i tak odpowiada 403.
 */
const CarOrderSuggestionsSection = () => {
  const r = useRecordContext<CarRecord>();
  return (
    <ModuleGate module="vehicleSuggestions" companyId={companyIdOf(r)}>
      <CarOrderSuggestions />
    </ModuleGate>
  );
};

const formatValue = (v: unknown, unit = ''): string =>
  v === null || v === undefined || v === '' ? '—' : `${String(v)}${unit}`;

const toText = (v: unknown): string | null =>
  typeof v === 'string' && v.trim() !== '' ? v : null;

/**
 * Nagłówek karty — zwykły tytuł strony, bez ramki (omówienie 01.10.2026):
 * numer rejestracyjny pogrubiony, model i chipy w jednej linii. Do tego dnia
 * stał tu osobny boks „Pojazd”, a kartoteka nie miała rejestracji ani modelu.
 * Bez rejestracji — nazwa z kartoteki, jak na liście floty.
 */
const CarTitle = () => {
  const r = useRecordContext<CarRecord>();
  if (!r) return null;
  const model = toText(r.makeAndModel);
  return (
    <Box
      data-testid="car-title"
      sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
    >
      <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }}>
        {toText(r.registrationNumber) ?? toText(r.name) ?? '—'}
      </Typography>
      {model ? (
        <Typography variant="body1" color="text.secondary">
          {model}
        </Typography>
      ) : null}
      <AvailabilityCell record={r} />
      <CompanyCell record={r} />
    </Box>
  );
};

/**
 * Spółka w kartotece pełną nazwą (na chipie w nagłówku — sam kraj). Brak
 * właściciela — to samo ostrzeżenie co na liście, nie pusty wiersz.
 */
const CompanyName = ({ record }: { record: CarRecord }) => {
  const company = record.company as { name?: unknown; country?: unknown } | null | undefined;
  if (!company) return <CompanyCell record={record} />;
  return <>{toText(company.name) ?? toText(company.country) ?? '—'}</>;
};

const CarRegistry = () => {
  const r = useRecordContext<CarRecord>();
  if (!r) return null;
  return (
    <SectionCard title="Kartoteka">
      <Table size="small">
        <TableBody>
          {/* Rejestracja, model, dostępność i spółka — w kartotece pełne
              (omówienie 01.10.2026), w nagłówku tylko skrót. Rejestracja bez
              zastępowania nazwą: pusty numer ma być widać. */}
          <Row label="Numer rejestracyjny">{formatValue(r.registrationNumber)}</Row>
          <Row label="Model">{formatValue(r.makeAndModel)}</Row>
          <Row label="Dostępność">
            <AvailabilityCell record={r} />
          </Row>
          <Row label="Spółka">
            <CompanyName record={r} />
          </Row>
          <Row label="Typ">{formatValue(vehicleTypeLabel(r.carType))}</Row>
          <Row label="Nadwozie">{formatValue(r.bodyType)}</Row>
          <Row label="Typ naczepy">{formatValue(r.trailerType)}</Row>
          <Row label="Rocznik">{formatValue(r.vehicleYear)}</Row>
          <Row label="Pierwsza rejestracja">
            {fmtCalendarDate(r.firstRegistrationDate as string)}
          </Row>
          <Row label="Kierowca">
            <DriverCell record={r} />
          </Row>
          <Row label="Drugi pojazd zestawu">
            <LinkedVehicleCell record={r} />
          </Row>
          <Row label="Opiekun FTL">{formatValue(r.ftlCaretaker)}</Row>
          <Row label="Identyfikator w TMS">{formatValue(r.tmsVehicleId)}</Row>
        </TableBody>
      </Table>
    </SectionCard>
  );
};

const CarTechnical = () => {
  const r = useRecordContext<CarRecord>();
  if (!r) return null;
  return (
    <SectionCard title="Dane techniczne">
      <Table size="small">
        <TableBody>
          <Row label="Ładowność">
            <PayloadCell record={r} />
          </Row>
          <Row label="Masa całkowita">{formatValue(r.totalWeightKg, ' kg')}</Row>
          {/* Jednostka to milimetry, mimo nazw kolumn — patrz `formatDimensions`. */}
          <Row label="Wymiary (dł. × szer. × wys.)">{formatDimensions(r)}</Row>
          <Row label="Liczba osi">{formatValue(r.numberOfAxles)}</Row>
          <Row label="Wózek">
            <ForkliftCell record={r} />
          </Row>
          {/* Przełącznik `manualAdr` (12.10.2026) — wyłączony: bez wiersza, jak w 0.31.3. */}
          <FeatureGate feature={MANUAL_ADR}>
            <Row label="ADR">
              <ManualAdr resource="cars" />
            </Row>
          </FeatureGate>
          <Row label="Przebieg">{formatValue(r.mileageKm, ' km')}</Row>
        </TableBody>
      </Table>
    </SectionCard>
  );
};

/**
 * Szczegóły pojazdu — tylko odczyt.
 *
 * Bez przycisku edycji świadomie: właścicielem kartoteki jest TMS klienta,
 * a nasza zmiana zniknęłaby przy najbliższym imporcie. Zmiany idą u nich.
 */
const CarShowPage = () => (
  <Show title="Pojazd" component={CarShowCard}>
    <Stack spacing={2} sx={{ p: 2 }}>
      <CarTitle />
      {/* Bez sekcji „Oferty na tym pojeździe” (omówienie 01.10.2026): czym
          auto jest zajęte, pokazuje harmonogram niżej. API ofert
          (`/car-schedule-offers`) zostaje bez zmian. */}
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' },
        }}
      >
        <CarRegistry />
        <CarTechnical />
        <CarEligibilities />
        {/* Bez „Zleceń serwisowych z TMS” od 16.10.2026 (pyt. 14). */}
        {/* Przełącznik `carChangeHistory` (12.10.2026) — wyłączony: bez sekcji, jak w 0.31.3. */}
        <FeatureGate feature={CAR_CHANGE_HISTORY}>
          <CarChangeHistory />
        </FeatureGate>
      </Box>
      {/* Harmonogram na całą szerokość (29.09.2026): oś dni z obsadzeniami
          i wariantami potrzebuje miejsca, a pół karty mieściło wcześniej
          tylko tabelkę okien miesięcznych. Podpowiedź zleceń też ma osiem
          kolumn, więc stoi pod nim. */}
      <VehicleSchedule />
      <CarOrderSuggestionsSection />
    </Stack>
  </Show>
);

/** Brak dostępu do floty — jedno zdanie zamiast pustej kartoteki (06.10.2026). */
export const NO_FLEET_ACCESS_TEXT = 'Nie masz dostępu do kartoteki floty.';

const NoFleetAccess = () => (
  <Alert severity="info" data-testid="no-fleet-access">
    {NO_FLEET_ACCESS_TEXT}
  </Alert>
);

/**
 * Karta pod `<Show>`. Gdy API odmówi (403), react-admin nie wylogowuje
 * (`authProvider.checkError`, `logoutUser: false`), tylko podstawia pusty
 * rekord bez `id` — sekcje pokazałyby wtedy same kreski, „brak danych”
 * i mylące „brak spółki”. Zamiast nich — komunikat o braku dostępu.
 */
const CarShowCard = ({ children, ...props }: CardProps) => {
  const r = useRecordContext<CarRecord>();
  const refused = r !== undefined && (r.id === undefined || r.id === null);
  return (
    <Card {...props}>
      {refused ? (
        <CardContent>
          <NoFleetAccess />
        </CardContent>
      ) : (
        children
      )}
    </Card>
  );
};

/**
 * Karta pojazdu tylko z dostępem do floty (`FTL_PLANNING` — tego samego
 * wymaga API `/cars`). Gość z bezpośredniego adresu (e2e 06.10.2026) widział
 * pustą kartotekę, kręcący się harmonogram i aktywne „Pokaż rekomendowane
 * zlecenia”; teraz — jeden komunikat, bez zapytań do API.
 */
export const CarShow = () => (
  <PermissionGate
    permission="FTL_PLANNING"
    element={ELEMENT.fleetCarCard}
    fallback={
      <Card sx={{ mt: 1 }}>
        <Title title="Pojazd" />
        <CardContent>
          <NoFleetAccess />
        </CardContent>
      </Card>
    }
  >
    <CarShowPage />
  </PermissionGate>
);
